import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { publicAPI } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import ErrorMessage from '../components/ErrorMessage'

import LastWeekSummary from '../components/LastWeekSummary'
import WeeklyTimeSeriesChart from '../components/WeeklyTimeSeriesChart'
import type { BrandSeries } from '../components/WeeklyTimeSeriesChart'
import { TrendingUp, Car, Users, Building2 } from 'lucide-react'
import { useYear } from '../contexts/YearContext'
import { useVehicleFilter } from '../contexts/VehicleFilterContext'
import { useAuth } from '../contexts/AuthContext'
import { useMineMerker } from '../hooks/useMineMerker'
import { localizeApiDetail } from '../i18n/apiError'

export default function Dashboard() {
  const { t } = useTranslation('dashboard')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [leasingStats, setLeasingStats] = useState<any>(null)
  const [ownershipStats, setOwnershipStats] = useState<any>(null)
  const [summaryStats, setSummaryStats] = useState<any>(null)
  const [weeklyDataRaw, setWeeklyDataRaw] = useState<any[]>([])
  const [brandWeeklyData, setBrandWeeklyData] = useState<Record<number, Record<string, number>>>({})
  const [prevYearData, setPrevYearData] = useState<any[]>([])
  const { selectedYear, getYearDateRange } = useYear()
  const { vehicleFilter, getVarebilParam } = useVehicleFilter()
  const { isAuthenticated } = useAuth()
  const { items: merkerItems } = useMineMerker()

  // Derive active brand series for the weekly chart
  const activeBrandSeries = useMemo<BrandSeries[]>(() => {
    if (!isAuthenticated) return []
    return merkerItems
      .filter(m => m.is_active)
      .map(m => ({ merke: m.merke, merke_navn: m.merke_navn }))
  }, [isAuthenticated, merkerItems])

  // Merge raw weekly data with brand data
  const weeklyData = useMemo(() => {
    if (!weeklyDataRaw.length) return weeklyDataRaw
    const activeMerkeNavn = merkerItems.filter(m => m.is_active).map(m => m.merke_navn)
    if (!activeMerkeNavn.length || !Object.keys(brandWeeklyData).length) return weeklyDataRaw
    return weeklyDataRaw.map((week: any) => {
      const brands = brandWeeklyData[week.week_number] || {}
      const extra: Record<string, number> = {}
      for (const m of activeMerkeNavn) {
        extra[`brand_${m}`] = brands[m] || 0
      }
      return { ...week, ...extra }
    })
  }, [weeklyDataRaw, brandWeeklyData, merkerItems])

  useEffect(() => {
    fetchDashboardData()
  }, [selectedYear, vehicleFilter])

  // Fetch brand weekly data when merkerItems become available
  useEffect(() => {
    const activeMerkeNavn = merkerItems.filter(m => m.is_active).map(m => m.merke_navn)
    if (!activeMerkeNavn.length) { setBrandWeeklyData({}); return }
    const varebilParam = getVarebilParam()
    publicAPI.getBrandWeeklyTimeSeries({
      year: parseInt(selectedYear),
      merke: activeMerkeNavn,
      varebil: varebilParam,
    }).then(res => {
      const lookup: Record<number, Record<string, number>> = {}
      for (const row of (res.data.data || [])) {
        if (!lookup[row.week_number]) lookup[row.week_number] = {}
        lookup[row.week_number][row.brand] = row.count
      }
      setBrandWeeklyData(lookup)
    }).catch(e => console.warn('Brand weekly data unavailable:', e))
  }, [merkerItems, selectedYear, vehicleFilter]) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDashboardData = async () => {
    try {
      setLoading(true)
      setError(null)

      const { startDate, endDate } = getYearDateRange()
      
      const start_date = startDate.toISOString().split('T')[0]
      const end_date = endDate.toISOString().split('T')[0]
      
      const varebilParam = getVarebilParam()
      const baseParams: any = { start_date, end_date }
      if (varebilParam !== undefined) {
        baseParams.varebil = varebilParam
      }

      const timeseriesParams: any = { year: parseInt(selectedYear) }
      if (varebilParam !== undefined) {
        timeseriesParams.varebil = varebilParam
      }

      // Fetch all data in parallel
      const [summaryRes, leasingRes, ownershipRes, weeklyRes] = await Promise.all([
        publicAPI.getSummary(baseParams),
        publicAPI.getLeasingStatistics(baseParams),
        publicAPI.getOwnershipStatistics(baseParams),
        publicAPI.getWeeklyTimeSeries(timeseriesParams),
      ])

      setSummaryStats(summaryRes.data.data)
      setLeasingStats(leasingRes.data.data)
      setOwnershipStats(ownershipRes.data.data)
      setPrevYearData(weeklyRes.data.prev_year_data || [])
      setWeeklyDataRaw(weeklyRes.data.data || [])
    } catch (err: any) {
      setError(localizeApiDetail(err.response?.data?.detail) || err.message || t('loadFailed'))
    } finally {
      setLoading(false)
    }
  }

  if (loading) return <LoadingSpinner />
  if (error) return <ErrorMessage message={error} />

  // Get EV percentage from summary stats
  const evPercentage = summaryStats?.ev_percentage 
    ? parseFloat(summaryStats.ev_percentage) 
    : 0

  // Get leasing percentage from summary stats
  const totalLeasingPercentage = summaryStats?.leasing_percentage
    ? parseFloat(summaryStats.leasing_percentage)
    : 0

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{t('title')}</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            {t('subtitle', { year: selectedYear })}
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{t('source')}</p>
        </div>
      </div>

      {/* ━━━ SECTION 1: Weekly Time-Series ━━━ */}
      {weeklyData.length > 0 ? (
        <WeeklyTimeSeriesChart data={weeklyData} prevYearData={prevYearData} year={parseInt(selectedYear)} vehicleFilter={vehicleFilter} brandSeries={activeBrandSeries} />
      ) : (
        <div className="card p-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            {t('weeklyTitle', { year: selectedYear })}
          </h2>
          <p className="text-gray-500 dark:text-gray-400">{t('noWeeklyData', { year: selectedYear })}</p>
        </div>
      )}

      {/* ━━━ SECTION 2: Last Week Pulse ━━━ */}
      <LastWeekSummary />

      {/* ━━━ SECTION 3: Year Statistics ━━━ */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
          {t('totalStats', { year: selectedYear })}
        </h2>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <SummaryCard
            title={t('cards.registrations')}
            value={leasingStats?.total_registrations?.toLocaleString('nb-NO') || '0'}
            icon={<Car />}
            color="blue"
          />
          <SummaryCard
            title={t('cards.leasingShare')}
            value={`${totalLeasingPercentage.toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})} %`}
            icon={<Users />}
            color="purple"
          />
          <SummaryCard
            title={t('cards.businessShare')}
            value={`${ownershipStats?.company_owned_share?.toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1}) || 0} %`}
            icon={<Building2 />}
            color="orange"
          />
          <SummaryCard
            title={t('cards.evs')}
            value={`${evPercentage.toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})} %`}
            icon={<TrendingUp />}
            color="green"
          />
        </div>
      </div>
    </div>
  )
}

function SummaryCard({ title, value, icon, color }: { title: string; value: string; icon: React.ReactNode; color: string }) {
  const colors = {
    blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
    green: 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400',
    purple: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
    orange: 'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400',
  }

  return (
    <div className="card p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{title}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</p>
        </div>
        <div className={`p-3 rounded-lg ${colors[color as keyof typeof colors]}`}>
          {icon}
        </div>
      </div>
    </div>
  )
}
